import { useEffect, useMemo, useRef, useState } from "react";

function CategoryFormModal({ isOpen, onClose, categoriaData, onSubmit }) {
  const isEdit = !!categoriaData;

  const initialForm = useMemo(
    () => ({
      nombre: categoriaData?.nombre || "",
      estado: categoriaData?.estado ?? 1,
      usuario_id: 1,
    }),
    [categoriaData]
  );

  const [form, setForm] = useState(initialForm);

  const [alert, setAlert] = useState(null);
  const alertTimerRef = useRef(null);

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setForm(initialForm);
    setAlert(null);
    setSaving(false);

    return () => {
      if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    };
  }, [initialForm, isOpen]);

  const showAlert = (type, message) => {
    setAlert({ type, message: String(message || "") });

    if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    alertTimerRef.current = window.setTimeout(() => setAlert(null), 4000);
  };

  const updateField = (e) => {
    const { name, value } = e.target;

    if (name === "estado") {
      setForm((prev) => ({ ...prev, [name]: Number(value) }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;

    setAlert(null);

    const cleanName = String(form.nombre || "").trim();
    if (!cleanName) {
      showAlert("error", "Category name is required.");
      return;
    }

    setSaving(true);

    try {
      await onSubmit({ ...form, nombre: cleanName });
      onClose?.();
    } catch (error) {
      console.error("Category save failed:", error);
      showAlert("error", error?.message || "Could not save category.");
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={ui.overlay} onMouseDown={(e) => {

      if (e.target === e.currentTarget && !saving) onClose?.();
    }}>
      <div style={ui.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div style={ui.header}>
          <h2 style={ui.title}>{isEdit ? "Edit Category" : "Create Category"}</h2>
          <button style={ui.closeBtn} onClick={onClose} disabled={saving} title="Close">
            ✕
          </button>
        </div>

        {alert && (
          <div
            style={{
              ...ui.alert,
              ...(alert.type === "error" ? ui.alertError : ui.alertInfo),
            }}
          >
            <span>{alert.message}</span>
            <button style={ui.alertX} onClick={() => setAlert(null)} disabled={saving}>
              ✕
            </button>
          </div>
        )}

        <form onSubmit={submit} style={ui.form}>
          <div style={ui.inputGroup}>
            <label style={ui.label}>Category Name</label>
            <input
              type="text"
              name="nombre"
              value={form.nombre}
              onChange={updateField}
              style={ui.input}
              required
              disabled={saving}
              autoFocus
            />
          </div>

          <div style={ui.inputGroup}>
            <label style={ui.label}>Status</label>
            <select
              name="estado"
              value={Number(form.estado)}
              onChange={updateField}
              style={ui.input}
              disabled={saving}
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>

          <div style={ui.footer}>
            <button type="button" style={ui.cancelBtn} onClick={onClose} disabled={saving}>
              Cancel
            </button>

            <button type="submit" style={{ ...ui.saveBtn, ...(saving ? ui.saveBtnDisabled : null) }} disabled={saving}>
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Category"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const ui = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
    zIndex: 2000,
  },

  modal: {
    width: "420px",
    background: "#ffffff",
    borderRadius: "14px",
    boxShadow: "0 6px 22px rgba(0,0,0,0.15)",
    overflow: "hidden",
  },

  header: {
    padding: "16px 24px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  title: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#1f2937",
  },

  closeBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "22px",
    color: "#6b7280",
    opacity: 0.85,
  },

  alert: {
    margin: "14px 22px 0",
    padding: "10px 12px",
    borderRadius: "10px",
    border: "1px solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    fontSize: "13px",
    fontWeight: 600,
  },
  alertError: {
    background: "#FEF2F2",
    borderColor: "#FECACA",
    color: "#7F1D1D",
  },
  alertInfo: {
    background: "#EFF6FF",
    borderColor: "#BFDBFE",
    color: "#1E3A8A",
  },
  alertX: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: "16px",
    opacity: 0.7,
  },

  form: {
    padding: "22px",
  },

  inputGroup: {
    marginBottom: "16px",
  },

  label: {
    display: "block",
    marginBottom: "5px",
    fontSize: "14px",
    fontWeight: 500,
    color: "#374151",
  },

  input: {
    width: "100%",
    padding: "11px 12px",
    borderRadius: "8px",
    border: "1px solid #d1d5db",
    background: "#ffffff",
    fontSize: "15px",
    color: "#1f2937",
    boxSizing: "border-box",
    outline: "none",
  },

  footer: {
    padding: "16px 24px",
    borderTop: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
  },

  cancelBtn: {
    padding: "10px 16px",
    background: "#e5e7eb",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    fontWeight: 500,
  },

  saveBtn: {
    padding: "10px 18px",
    background: "#2563eb",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 600,
  },

  saveBtnDisabled: {
    opacity: 0.75,
    cursor: "not-allowed",
  },
};

export default CategoryFormModal;
